import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../../ui/chart';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import { CampaignReportData } from '../../../types/campaignReport';
import { formatPercent } from '../../../lib/reportFormatters';
import { Badge } from '../../ui/badge';
import { Lightbulb } from 'lucide-react';

interface TimeOfDayTabProps {
  data: CampaignReportData;
}

const chartConfig: ChartConfig = {
  sales: {
    label: 'Sales %',
    color: '#183df7',
  },
};

export function TimeOfDayTab({ data }: TimeOfDayTabProps) {
  const chartData = [
    { name: '10am-1pm', sales: data.timeOfDay.sales10am1pm },
    { name: '1pm-4pm', sales: data.timeOfDay.sales1pm4pm },
    { name: '4pm-7pm', sales: data.timeOfDay.sales4pm7pm },
    { name: '7pm-9pm', sales: data.timeOfDay.sales7pm9pm },
  ];

  const peakWindow = data.timeOfDay.peakWindow || '4pm-7pm';

  return (
    <div className="space-y-8">
      {/* Sales by Time of Day Table */}
      <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
        <CardHeader>
          <CardTitle className="text-xl font-bold" style={{ color: '#183df7' }}>
            7️⃣ Sales by Time of Day
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Time</TableHead>
                <TableHead className="text-right font-semibold">% of Sales</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">10am-1pm</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(data.timeOfDay.sales10am1pm)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">1pm-4pm</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(data.timeOfDay.sales1pm4pm)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">4pm-7pm</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(data.timeOfDay.sales4pm7pm)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">7pm-9pm</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(data.timeOfDay.sales7pm9pm)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
        <CardHeader>
          <CardTitle className="text-lg font-semibold" style={{ color: '#183df7' }}>
            Sales Distribution by Time of Day
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-[300px]">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12, fill: '#183df7', fontWeight: 500 }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#183df7', fontWeight: 500 }}
                tickFormatter={(value) => `${value}%`}
              />
              <ChartTooltip 
                content={<ChartTooltipContent formatter={(value: any) => formatPercent(Array.isArray(value) ? value[0] : value)} />}
              />
              <Bar dataKey="sales" fill="#183df7" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Peak Window & Recommendation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
          <CardContent className="p-6 text-center">
            <p className="text-sm font-medium text-muted-foreground mb-3">Peak Conversion Window</p>
            <Badge className="text-lg px-6 py-3 bg-blue-600 text-white">
              {peakWindow}
            </Badge>
          </CardContent>
        </Card>

        <Card 
          className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100/50"
          style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}
        >
          <CardContent className="p-6 flex items-start gap-3">
            <Lightbulb className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: '#183df7' }} />
            <div>
              <p className="font-semibold mb-2" style={{ color: '#183df7' }}>Recommendation</p>
              <p className="text-sm text-gray-700">
                Consider increasing staffing or running targeted promotions during the peak conversion window ({peakWindow}) to maximize sales.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
